import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { animated } from '@react-spring/web';
import pluralize from 'pluralize';

import { hideSideMenu } from 'src/redux/actions/hub';
import { fetchAssessmentConfigurations } from 'src/redux/actions/coaching/settings';

import Actions from 'src/components/actionbar';
import ScoreCardPreview from './preview';

import { AssessmentConfiguration, AssessmentParameter } from 'src/types/coaching/simulation';

import _ from "lodash";
import cx from "classnames";
import styles from "./scoreCards.module.scss"

const ScoreCardsPage: React.FC = () => {

  const dispatch = useDispatch();

  const { loading, scorecards} = useSelector((state) => {
    const scoreCardsState = _.get(state, 'coaching.settings.scorecards');

    const scorecards: AssessmentConfiguration[] = _.get(scoreCardsState, 'items') || [];
    const loading = _.get(scoreCardsState, 'loading');

    return {
      scorecards,
      loading,
    };
  }, _.isEqual);

  let defaultScorecard =
    scorecards.find(s => !s.systemDefined && s.defaultConfiguration);

  if (!defaultScorecard) {
    defaultScorecard =
      scorecards.find(s => s.systemDefined && s.defaultConfiguration);
  }
    
  const [selectedScoreCard, setSelectedScoreCard] = useState<AssessmentConfiguration>();
  const [showPreview, setShowPreview] = useState(false);

  const openPreview = (item: any) => {
    setSelectedScoreCard(item);
    dispatch(hideSideMenu(true));
    setShowPreview(true);
  };

  const closePreview = () => {
    dispatch(hideSideMenu(false));
    setShowPreview(false);
  }

  useEffect(() => {
    dispatch(fetchAssessmentConfigurations());
  }, []); 

  if (showPreview && selectedScoreCard) {
    return <ScoreCardPreview scorecard={selectedScoreCard} onClick={closePreview} defaultScoreCard={defaultScorecard} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.top}>
        <div className={styles.header}>
          <h1 className={styles.title}>call assessment frameworks</h1>
          <p>
           Define the evaluation criteria used to assess learner performance across call simulations and real calls.
          </p>
        </div>
      </div>
      <div className={styles.body}>
        {loading && <PlaceholderScoreCard />}
        {!loading &&
          scorecards.map((scorecard: AssessmentConfiguration) => (
            <ScoreCard
              key={scorecard.id}
              scorecard={scorecard}
              defaultScoreCard={defaultScorecard}
              onClick={() => openPreview(scorecard)}
            />
          ))}
      </div>
    </div>
  );
};

export default ScoreCardsPage;

interface ScoreCardProps {
  scorecard: AssessmentConfiguration;
  onClick: () => void;
  defaultScoreCard?: AssessmentConfiguration | null;
}

const ScoreCard: React.FC<ScoreCardProps> = ({ scorecard, onClick, defaultScoreCard }) => {
  const parameters = _.get(scorecard, 'parameters', []) as AssessmentParameter[];
  const parameterCount = _.size(parameters);

  let subParameterCount = 0;
  parameters.forEach(p => {
    subParameterCount += _.size(p.parameters);
  });

  const isDefault = scorecard.id === defaultScoreCard?.id;

  return (
    <div className={styles.card} onClick={onClick}>
      <div className={styles.header}>
        <div className={styles.status}>
          <div className={styles.isActive}>
          </div>
          <p>Active</p>
        </div>
        <div className={styles.type}>
          <div className={styles.callType}>all type</div>
          {isDefault && <div className={styles.callType}>default</div>}
        </div>
      </div>
      <div className={styles.information}>
        {<div className={styles.name}>{scorecard.name}</div>}
        <div className={styles.metadata}>
          <div className={styles.metaItem}>
            <div className={styles.value}>{parameterCount}</div>
            <div className={styles.label}>{pluralize("parameter", parameterCount)}
            </div>
          </div>
          <div className={styles.divider} />
          <div className={styles.metaItem}>
            <div className={styles.value}>{subParameterCount}</div>
            <div className={styles.label}>{pluralize("sub-parameter", subParameterCount)}</div>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
       predefined sales methodology framework.
      </div>
    </div>
  )
}

const PlaceholderScoreCard = ({ count = 3 }) => {
  return (
    <div className={styles.wrapper}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className={styles.placeholderCard}>
          <div className={styles.header}>
            <div className={styles.status} />
          </div>
          <div className={styles.sectionTitle} />
          <div className={styles.metricsRow}>
            <div className={styles.metricBlock}>
              <div className={styles.metric} />
              <div className={styles.metricLabel} />
            </div>
            <div className={styles.divider} />

            <div className={styles.metricBlock}>
              <div className={styles.metric} />
              <div className={styles.metricLabel} />
            </div>
          </div>
          <div className={styles.footer} />
        </div>
      ))}
    </div>
  );
};